import React, { useState, useContext, useEffect, useMemo } from "react";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  Typography,
  Grid,
  Container,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  InputAdornment,
  Paper,
  Alert,
} from "@mui/material";
import {
  Check as CheckIcon,
  Star as StarIcon,
  Rocket as RocketIcon,
  Business as BusinessIcon,
  LocalOffer as CouponIcon,
  CheckCircle as SuccessIcon,
} from "@mui/icons-material";
import { userContext } from "../../stores/users/userContext";
import { messageContext } from "../../stores/messages/messageContext";
import { hostApi } from "../constants";
import { getPlanByNameUrl } from "../constants";
import { ROUTES } from "../../constants/routes";
import apiRequest from "../../utils/apiClient";
import brandLogo from "../../assets/brand/logo_site.svg";
import { POST_LOGIN_INTENT_KEY } from "../../constants/postLoginIntent";
import { formatDurationShort } from "../../utils/limitErrorHandler";

// CherryClipz brand — the subscribe page reads on-brand cherry, not MUI default blue.
const CHERRY = "#ec2b57";
const CHERRY_SOFT = "#ff6f8f";
// Mono "spreadsheet" utility face — the signature that ties the page to the product
// (a queryable sheet of clips). Used for eyebrows, plan names, labels. System mono, no webfont.
const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';
// The free tier is not a purchase — no Stripe session, nothing to check out.
// Arriving from a refused upload (?video=<seconds>&left=<seconds>, see limitErrorHandler):
// say why you're here, so the page reads as the answer to your upload, not as an ad.
const limitNoteFrom = (search) => {
  const q = new URLSearchParams(search || "");
  const video = Number(q.get("video"));
  if (!Number.isFinite(video) || video <= 0) return "";
  const left = Number(q.get("left"));
  const why = Number.isFinite(left) && left >= 60
    ? `Your video is ${formatDurationShort(video)}, longer than the ${formatDurationShort(left)} of transcription you have left.`
    : `Your video is ${formatDurationShort(video)}, and your transcription time is used up.`;
  return `${why} Pick a plan below to upload it.`;
};
const isFreePlan = (plan) =>
  String(plan?.name || plan?.id || "").trim().toLowerCase() === "free" ||
  Number(plan?.price) === 0;

// Clean price: $29 not $29.00; keep cents only when they exist.
const fmtPrice = (n) =>
  Number.isInteger(Number(n)) ? `$${Number(n)}` : `$${Number(n).toFixed(2)}`;
// Fallback feature bullets when the backend Plan has no `features` set, so the cards
// aren't sparse. Keyed by plan name. Matches the live free/pro limits.
// KEEP IN SYNC with the ResourcesByPlan rows in Django admin (Emo_Users > Resources by
// plans) — those are what actually gets enforced; this is only what people are promised.
//
// HONESTY NOTE — what is actually gated:
//   * transcription hours, storage and exports/month (plan_limits.py).
// The Premiere panel is NOT gated since 2026-10-25: any signed-in account may download it, and
// what it transcribes counts against the same hours. So every plan lists it.
// Nothing else keys off plan name. Do NOT list cloud posters or Black Cherry as Pro perks —
// free accounts get those. And there are no "AI credits" to offer: Black Cherry calls the
// user's OWN key from the browser (draggableToolbar.jsx callLLM); the server-side key is
// used ONLY for guests on /s/ share links.
const DEFAULT_PLAN_FEATURES = {
  free: [
    "1 free hour of transcription to start — it does not renew",
    "10 GB storage · 50 exports / month",
    "The full spreadsheet — filters, sorting, exports",
    "Black Cherry AI — personas, filler removal, scoring",
    "Bring your own AI key: OpenAI, Claude or Gemini",
    // Say "sheet in a browser" so this can't be mistaken for the phone → Premiere link.
    // This one is create_share / the public /s/:token route: a web-only, no-login view of
    // your clips. Nothing to do with the panel.
    "Share your clip sheet in a browser — no login needed",
    "The Premiere Pro panel, on Mac and Windows",
  ],
  // The $10 rung (2026-09-08). Same product as Free with five times the hours.
  creator: [
    "5 hours of transcription / month",
    "20 GB storage · 100 exports / month",
    "The full spreadsheet — filters, sorting, exports",
    "Black Cherry AI — personas, filler removal, scoring",
    "Bring your own AI key: OpenAI, Claude or Gemini",
    "Share your clip sheet in a browser — no login needed",
    "The Premiere Pro panel, on Mac and Windows",
    "Everything in Free, with five times the hours",
  ],
  // Pro is the most hours (the panel is on every plan since 2026-10-25).
  pro: [
    "15 hours of transcription / month",
    "100 GB storage — 10× the room",
    "500 exports / month",
    "Priority support — a real person, usually same day",
    "The Premiere Pro panel, on Mac and Windows",
    "Everything in Free, included",
  ],
};
// Where each plan works, said once and plainly, so nobody has to infer it from the bullets.
// This is the whole product line in two words per card.
const PLAN_SCOPE = {
  free: "Web + Premiere",
  creator: "Web + Premiere",
  pro: "Web + Premiere",
};


/**
 * The mark above each plan card: the cherry on its own for Free, and the CherryClipz +
 * Premiere Pro lockup for Pro — the same pairing the plugin page uses, so the two pages
 * feel like one product.
 */
const PlanLockup = ({ withPremiere }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.25,
      mb: 2,
      minHeight: 44,
    }}
  >
    <Box component="img" src={brandLogo} alt="" sx={{ height: 34, width: "auto" }} />
    {withPremiere && (
      <>
        <Box component="span" sx={{ fontSize: "1.4rem", color: "text.disabled", fontWeight: 300 }}>
          +
        </Box>
        <Box
          component="span"
          aria-label="Premiere Pro"
          sx={{
            display: "grid",
            placeItems: "center",
            width: 36,
            height: 36,
            borderRadius: "9px",
            bgcolor: "#2b0a52",
            color: "#c9b3ff",
            border: "1px solid #4a2a7a",
            fontWeight: 800,
            fontSize: "0.95rem",
            letterSpacing: "-0.02em",
          }}
        >
          Pr
        </Box>
      </>
    )}
  </Box>
);

const plans_old = [
  {
    id: "Starter",
    name: "Starter",
    price: 10,
    currency: "USD",
    interval: "month",
    description: "Perfect for getting started",
    features: [
      "3 GB of transcribed videos",
      "Files kept while subscription is active",
      "Community support via Discord",
      "MP4, MP3 video export formats",
      "Standard transcription accuracy",
    ],
    buttonText: "Get Started Now",
    buttonVariant: "outlined",
    popular: false,
    icon: <CheckIcon />,
  },
  {
    id: "Basic",
    name: "Basic",
    price: 20,
    currency: "USD",
    interval: "month",
    description: "Great for regular users",
    features: [
      "5 GB of transcribed videos",
      "Files kept while subscription is active",
      "Email support",
      "MP4, MP3, GIF video export formats",
      "Standard transcription accuracy",
      "Basic editing tools",
    ],
    limitations: [],
    buttonText: "Choose Basic",
    buttonVariant: "outlined",
    popular: false,
    icon: <RocketIcon />,
  },
  {
    id: "Intermediate",
    name: "Intermediate",
    price: 35,
    currency: "USD",
    interval: "month",
    description: "Most popular for hobbyists and professionals",
    features: [
      "10 GB of transcribed videos",
      "Files kept while subscription is active",
      "Priority email support",
      "MP4, MP3, GIF, SRT, VTT, and more video export formats",
      "Export to multiple formats",
    ],
    limitations: [],
    buttonText: "Start Intermediate",
    buttonVariant: "contained",
    popular: true,
    icon: <StarIcon />,
  },
  {
    id: "Pro",
    name: "Pro",
    price: 99,
    currency: "USD",
    interval: "month",
    description: "For professionals and businesses",
    features: [
      "Unlimited GB of transcribed videos",
      "Files kept while subscription is active",
      "Priority email & phone support",
      "MP4, MP3, GIF, SRT, VTT, and more video export formats",
      "API access",
    ],
    limitations: [],
    buttonText: "Start Pro",
    buttonVariant: "outlined",
    popular: false,
    icon: <BusinessIcon />,
    isContactSales: false,
  },
];

const getIcons = (icon) => {
  switch (icon) {
    case "Starter":
      return <CheckIcon />;
    case "Basic":
      return <RocketIcon />;
    case "Intermediate":
      return <StarIcon />;
    case "P ro":
      return <BusinessIcon />;
    default:
      return <CheckIcon />;
  }
};

const SubscriptionPlans = () => {
  const [loading, setLoading] = useState(null);
  const userStore = useContext(userContext);
  const messageStore = useContext(messageContext);
  const location = useLocation();
  const limitNote = limitNoteFrom(location.search);
  // NOTE: there used to be a `buttonEnabled` flag here that was inverted (it was passed to
  // `disabled`), shared across BOTH plan cards, set on every click, and only cleared at the
  // very end of handleSubscribe — so any early return (not logged in, free plan, already on
  // Pro) left every button permanently dead until a page reload. `loading` already tracks
  // the in-flight plan and is cleared in a finally, so it does this job correctly.

  // Coupon-related state
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [plans, setPlans] = useState([]);

  // Recolor MUI's default (blue) primary to cherry for this page only, keeping the
  // app's existing dark palette. Everything using color="primary" turns cherry.
  const baseTheme = useTheme();
  const cherryTheme = useMemo(
    () => createTheme(baseTheme, { palette: { primary: { main: CHERRY, contrastText: "#fff" } } }),
    [baseTheme],
  );
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlans = async () => {
      const response = await apiRequest(getPlanByNameUrl);
      const data = await response.json();
      console.log("data", data);
      let plans = [];
      data.plans.forEach((plan) => {
        const planData = {
          id: plan.name,
          name: plan.name,
          price: plan.price,
          currency: plan.currency,
          interval: plan.interval,
          description: plan.description,
          features: plan.features ? plan.features.split("\n") : [],
          limitations: plan.limitations ? plan.limitations.split("\n") : [],
          buttonText: plan.buttonText,
          buttonVariant: plan.buttonVariant,
          popular: plan.popular,
          icon: getIcons(plan.name),
          isContactSales: plan.isContactSales,
        };
        // check if is is already in the plans
        if (!plans.some((p) => p.id === plan.id)) {
          plans.push(planData);
        }
        setPlans(plans);
      });
    };
    fetchPlans();
  }, []);

  // Helper function to calculate discounted price
  const calculateDiscountedPrice = (originalPrice, coupon) => {
    if (!coupon || originalPrice === 0) return originalPrice;

    if (coupon.type === "percentage") {
      return originalPrice * (1 - coupon.value / 100);
    } else if (coupon.type === "fixed") {
      return Math.max(0, originalPrice - coupon.value);
    }
    return originalPrice;
  };

  // Function to validate and apply coupon
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }

    setCouponLoading(true);
    setCouponError("");

    try {
      const formData = new FormData();
      formData.append("coupon_code", couponCode.trim());

      const response = await apiRequest(`${hostApi}validate-coupon/`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Invalid coupon code");
      }

      setAppliedCoupon({
        code: couponCode.trim(),
        type: result.type, // 'percentage' or 'fixed'
        value: result.value, // percentage (e.g., 20) or fixed amount (e.g., 5)
        description:
          result.description ||
          `${result.value}${result.type === "percentage" ? "%" : "$"} off`,
      });

      messageStore.setMessage("Coupon applied successfully!", "success");
    } catch (err) {
      console.error("Coupon validation error:", err);
      setCouponError(err.message);
    } finally {
      setCouponLoading(false);
    }
  };

  // Function to remove applied coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
  };

  const handleSubscribe = async (plan) => {
    if (plan.isContactSales) {
      window.location.href = "/contact/";
      return;
    }
    // handle all other plans
    if (!userStore.isUserAuth()) {
      messageStore.setMessage("Please login/signup to subscribe", "error");
      // Remember WHY they're being sent to login. Without this they sign up, get dropped on
      // the homepage, and have to find their way back to pricing on their own — we were
      // throwing away the one visitor who had already decided to pay.
      try { sessionStorage.setItem(POST_LOGIN_INTENT_KEY, ROUTES.PRICING); } catch (e) { /* ignore */ }
      setTimeout(() => {
        // Was "/signup/", which is not a route — it 404'd.
        window.location.href = ROUTES.LANDING;
      }, 2000);
      return;
    }
    // The free card isn't a purchase — there's nothing to check out. Send them in.
    if (isFreePlan(plan)) {
      navigate(ROUTES.HOME);
      return;
    }
    // MUST be isUserOnPaidPlan, not isUserStripeActive: every free account has an active
    // StripeCustomer row (ensure_free_plan), so the old check told every free user "You are
    // already subscribed" and blocked the only path to paying us.
    if (userStore.isUserOnPaidPlan()) {
      messageStore.setMessage("You're already subscribed — change or manage it here", "info");
      setTimeout(() => {
        // Was "/downloaded/", which is not a route.
        window.location.href = ROUTES.SUBSCRIPTION_MANAGE;
      }, 1500);
      return;
    }

    setLoading(plan.id);

    try {
      // Create checkout session via Django backend
      const formData = new FormData();
      formData.append("plan", plan.id);

      // Include coupon code if applied
      if (appliedCoupon) {
        formData.append("coupon_code", appliedCoupon.code);
      }

      const response = await apiRequest(`${hostApi}create-checkout-session/`, {
        method: "POST",
        body: formData,
      });

      const session = await response.json();

      if (!response.ok) {
        throw new Error(session.error || "Failed to create checkout session");
      }

      // Redirect to Stripe Checkout using the session URL
      if (session.url) {
        window.location.href = session.url;
      } else {
        throw new Error("No checkout URL provided by server");
      }
    } catch (err) {
      console.error("Subscription error:", err);
      messageStore.setMessage(err.message, "error");
    } finally {
      setLoading(null);
    }
  };

  return (
    <ThemeProvider theme={cherryTheme}>
    <Box
      sx={{
        minHeight: "100%",
        backgroundImage:
          "radial-gradient(1100px 520px at 78% -8%, rgba(236,43,87,0.10), transparent 60%)",
      }}
    >
    <Box
      component="nav"
      sx={{
        maxWidth: "1152px",
        mx: "auto",
        px: { xs: 2, md: 3 },
        py: 2.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box
        onClick={() => navigate("/")}
        sx={{ display: "flex", alignItems: "center", gap: "9px", cursor: "pointer" }}
      >
        {/* The real mark. This used to be a CSS blob approximating a cherry. */}
        <Box component="img" src={brandLogo} alt="" sx={{ height: 26, width: "auto" }} />
        <Typography sx={{ fontWeight: 750, fontSize: "1.05rem", letterSpacing: "-0.01em" }}>
          Cherry<Box component="span" sx={{ color: CHERRY }}>Clipz</Box>
        </Typography>
      </Box>
      <Button
        onClick={() => navigate("/")}
        sx={{ color: "text.secondary", textTransform: "none", fontWeight: 600, "&:hover": { color: CHERRY_SOFT } }}
      >
        ← Back to app
      </Button>
    </Box>
    <Container maxWidth="lg" sx={{ py: 8, pt: 2 }}>
      <Box textAlign="center" mb={6}>
        {limitNote && (
          <Box
            role="status"
            sx={{
              maxWidth: 640,
              mx: "auto",
              mb: 3,
              px: 2.5,
              py: 1.5,
              borderRadius: "12px",
              border: `1px solid ${CHERRY}`,
              bgcolor: "rgba(236,43,87,0.10)",
              fontWeight: 650,
              lineHeight: 1.45,
            }}
          >
            {limitNote}
          </Box>
        )}
        <Typography
          sx={{
            fontFamily: MONO,
            fontSize: "0.8rem",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: CHERRY_SOFT,
            mb: 1.5,
          }}
        >
          Pricing
        </Typography>
        <Typography
          variant="h2"
          component="h1"
          sx={{ fontWeight: 820, letterSpacing: "-0.02em", lineHeight: 1.05, mb: 2, textWrap: "balance" }}
        >
          Free to start. Pro when you're rolling.
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: "auto", mb: 2.5, fontWeight: 400 }}>
          Every quote and every silence becomes its own clip. Start free, on the web or in
          Premiere Pro.
        </Typography>
        {/* Say it before anyone pays, not after: which computers the panel runs on. */}
        <Chip
          label="Premiere panel: Mac and Windows"
          size="small"
          variant="outlined"
          sx={{ mb: 4, color: "text.secondary", borderColor: "rgba(255,255,255,0.18)" }}
        />

      </Box>

      <Grid container spacing={4} alignItems="stretch" justifyContent="center" sx={{ maxWidth: 1080, mx: "auto" }}>
        {plans.map((plan) => {
          const originalPrice = plan.price;
          const discountedPrice = calculateDiscountedPrice(
            originalPrice,
            appliedCoupon,
          );
          const hasDiscount =
            appliedCoupon &&
            originalPrice > 0 &&
            discountedPrice < originalPrice;

          return (
            <Grid item xs={12} sm={6} md={4} key={plan.id}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: "18px",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  border: plan.popular ? "2px solid" : "1px solid",
                  borderColor: plan.popular ? CHERRY : "divider",
                  boxShadow: plan.popular
                    ? `0 0 0 1px ${CHERRY}, 0 20px 50px -24px rgba(0,0,0,.7)`
                    : undefined,
                  "&:hover": {
                    transform: "translateY(-4px)",
                    transition: "transform 0.2s ease-in-out",
                    boxShadow: 4,
                  },
                }}
              >
                {plan.popular && (
                  <Chip
                    label="Most Popular"
                    color="primary"
                    sx={{
                      position: "absolute",
                      top: -12,
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontWeight: "bold",
                    }}
                  />
                )}

                {/* Discount badge for non-free plans */}
                {hasDiscount && (
                  <Chip
                    label={`Save $${(originalPrice - discountedPrice).toFixed(2)}`}
                    color="error"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: plan.popular ? 120 : 8,
                      right: 16,
                      fontWeight: "bold",
                      fontSize: "0.75rem",
                      zIndex: 1,
                    }}
                  />
                )}

                {/* Plan name + description */}
                <Box sx={{ px: 3.5, pt: plan.popular ? 4.5 : 4, pb: 0.5, textAlign: "center" }}>
                  {/* Free carries the cherry alone; Pro carries the CherryClipz + Premiere
                      lockup, the way the plugin page does. Note both plans include the panel
                      — the lockup marks which tier is built for a real Premiere workload,
                      not which one can install it. */}
                  {/* Every plan has the Premiere panel now (2026-10-25), so no card wears the Pr mark alone. */}
                  <PlanLockup withPremiere={false} />
                  <Typography
                    sx={{
                      fontFamily: MONO,
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      color: plan.popular ? CHERRY_SOFT : "text.secondary",
                    }}
                  >
                    {plan.name}
                  </Typography>
                  {/* Where the plan works, stated outright — the difference people most need
                      to see, and the one you'd otherwise have to infer from the bullets. */}
                  {PLAN_SCOPE[String(plan.name).toLowerCase()] && (
                    <Chip
                      label={PLAN_SCOPE[String(plan.name).toLowerCase()]}
                      size="small"
                      variant={plan.popular ? "filled" : "outlined"}
                      sx={{
                        mt: 1.25,
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        ...(plan.popular
                          ? { bgcolor: CHERRY, color: "#fff" }
                          : { color: "text.secondary", borderColor: "rgba(255,255,255,0.2)" }),
                      }}
                    />
                  )}
                  {plan.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1.25, minHeight: 40, lineHeight: 1.45 }}
                    >
                      {plan.description}
                    </Typography>
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1, px: 3.5, pt: 1 }}>
                  <Box textAlign="center" mb={3}>
                    {hasDiscount && (
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ textDecoration: "line-through", lineHeight: 1, mb: 0.5 }}
                      >
                        {fmtPrice(originalPrice)}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 0.75 }}>
                      <Typography
                        component="span"
                        sx={{ fontSize: "3rem", fontWeight: 820, letterSpacing: "-0.03em", color: "#fff", lineHeight: 1.02 }}
                      >
                        {fmtPrice(hasDiscount ? discountedPrice : plan.price)}
                      </Typography>
                      <Typography component="span" sx={{ fontSize: "1rem", fontWeight: 500, color: "text.secondary" }}>
                        / {String(plan.interval || "month").replace(/monthly/i, "month").toLowerCase()}
                      </Typography>
                    </Box>
                    {plan.trialDays && (
                      <Typography variant="body2" sx={{ color: CHERRY_SOFT, fontWeight: 700, mt: 1 }}>
                        {plan.trialDays}-day free trial
                      </Typography>
                    )}
                  </Box>

                  <List dense sx={{ py: 0 }}>
                    {(plan.features && plan.features.length
                      ? plan.features
                      : DEFAULT_PLAN_FEATURES[String(plan.name).toLowerCase()] || []
                    ).map((feature, index) => (
                      <ListItem key={index} sx={{ py: 0.65, px: 0 }} alignItems="flex-start">
                        <ListItemIcon sx={{ minWidth: 30, mt: 0.4 }}>
                          <CheckIcon sx={{ color: CHERRY_SOFT }} fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ fontSize: "0.94rem", color: "text.secondary" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>

                <CardActions sx={{ p: 3.5, pt: 1 }}>
                  <Button
                    fullWidth
                    variant={plan.popular ? "contained" : "outlined"}
                    onClick={() => handleSubscribe(plan)}
                    disabled={loading !== null}
                    startIcon={
                      loading === plan.id ? <CircularProgress size={20} color="inherit" /> : null
                    }
                    sx={{
                      py: 1.7,
                      borderRadius: "12px",
                      fontWeight: 800,
                      fontSize: "1.02rem",
                      textTransform: "none",
                      ...(plan.popular
                        ? {
                            bgcolor: CHERRY,
                            color: "#fff",
                            boxShadow: `0 12px 26px -12px ${CHERRY}`,
                            "&:hover": { bgcolor: "#ff2a5a", boxShadow: `0 14px 30px -12px ${CHERRY}` },
                          }
                        : {
                            borderColor: "rgba(255,255,255,0.22)",
                            color: "#fff",
                            borderWidth: "1.5px",
                            "&:hover": { borderColor: CHERRY, color: CHERRY_SOFT, borderWidth: "1.5px" },
                          }),
                    }}
                  >
                    {loading === plan.id ? "Processing..." : plan.buttonText}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* One product, three places you touch it. True today: a panel upload lands in the
          same library, posters and all, so the phone and the web see it too. */}
      <Box textAlign="center" mt={7} maxWidth={640} mx="auto">
        <Typography
          sx={{
            fontFamily: MONO,
            fontSize: "0.72rem",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "text.secondary",
            mb: 1.5,
          }}
        >
          One account, everywhere you work
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          Transcribe from the panel in Premiere and it lands in the same library — clips,
          posters and all. Open it later on your phone or on the web, keep cutting, export
          from wherever you are.
        </Typography>
      </Box>

      {/* Below the plans on purpose: a coupon field above the pricing sends people off to
          hunt for a code they don't have. */}
      <Box maxWidth="sm" mx="auto" mt={6}>
        {!appliedCoupon ? (
          <Box display="flex" gap={2} alignItems="flex-start">
            <TextField
              fullWidth
              size="small"
              placeholder="Discount code (optional)"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              error={!!couponError}
              helperText={couponError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CouponIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  applyCoupon();
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={applyCoupon}
              disabled={couponLoading || !couponCode.trim()}
              startIcon={couponLoading ? <CircularProgress size={16} /> : null}
              sx={{ minWidth: 100 }}
            >
              {couponLoading ? "Checking..." : "Apply"}
            </Button>
          </Box>
        ) : (
          <Alert
            severity="success"
            action={
              <Button size="small" onClick={removeCoupon}>
                Remove
              </Button>
            }
            icon={<SuccessIcon />}
          >
            <strong>{appliedCoupon.code}</strong> - {appliedCoupon.description}
          </Alert>
        )}
      </Box>

      <Box textAlign="center" mt={6}>
        {/* Replaces a "30-day money-back guarantee" we were advertising but don't offer.
            This one is true: Manage Subscription has a Cancel button, no email required. */}
        <Typography variant="body2" color="text.secondary" mb={2}>
          Cancel anytime from your account — no email, no phone call.
        </Typography>
        {/* The panel had no route in from anywhere public — you had to be sent a link. */}
        <Typography variant="body2" color="text.secondary" mb={2}>
          Editing in Premiere?{" "}
          <Button variant="text" onClick={() => navigate(ROUTES.PLUGIN_DOWNLOAD)}>
            Get the Premiere panel
          </Button>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Questions before you buy?{" "}
          <Button variant="text" onClick={() => navigate(ROUTES.CONTACT)}>
            Talk to us
          </Button>
        </Typography>
      </Box>
    </Container>
    </Box>
    </ThemeProvider>
  );
};

export default SubscriptionPlans;
